import { isProduction } from "@/helpers/envHelper"
import {
  IEmailTemplateSyncPayload,
  SyncEnvironment,
} from "@/models/admin/emailTemplates/emailTemplates.types"

const NOVA_URLS: Record<SyncEnvironment, string> = {
  [SyncEnvironment.STAGING]: "https://nova.staging.soenergy.co",
  [SyncEnvironment.PRODUCTION]: "https://nova.so.energy",
}

export const SYNC_ROUTE_PATH = "/admin/email-templates/sync"

// Local .env files may set VITE_VERCEL_ENV=production; a dev server still talks to staging.
function isProductionDeployment(): boolean {
  return isProduction() && !import.meta.env.DEV
}

export function currentSyncEnvironment(): SyncEnvironment {
  return isProductionDeployment()
    ? SyncEnvironment.PRODUCTION
    : SyncEnvironment.STAGING
}

export function syncTargetEnvironment(): SyncEnvironment {
  return isProductionDeployment()
    ? SyncEnvironment.STAGING
    : SyncEnvironment.PRODUCTION
}

export function toSyncPayload(
  template: Omit<IEmailTemplateSyncPayload, "makeLive" | "sourceEnvironment">,
  makeLive: boolean,
): IEmailTemplateSyncPayload {
  return {
    communicationName: template.communicationName,
    subject: template.subject,
    htmlContent: template.htmlContent,
    plainContent: template.plainContent,
    modifiedReason: template.modifiedReason || null,
    makeLive,
    sourceEnvironment: currentSyncEnvironment(),
  }
}

const SYNC_NOTES = Object.values(SyncEnvironment).flatMap((environment) => [
  ` (synced from ${environment})`,
  `synced from ${environment}`,
])

function stripSyncNotes(reason: string): string {
  let stripped = reason.trim()
  let note = SYNC_NOTES.find((n) => stripped.toLowerCase().endsWith(n))
  while (note) {
    stripped = stripped.slice(0, -note.length).trim()
    note = SYNC_NOTES.find((n) => stripped.toLowerCase().endsWith(n))
  }
  return stripped
}

/** Replaces, rather than appends to, a note left by an earlier sync. */
export function syncedModifiedReason(
  payload: IEmailTemplateSyncPayload,
): string {
  const reason = stripSyncNotes(payload.modifiedReason ?? "")
  return reason
    ? `${reason} (synced from ${payload.sourceEnvironment})`
    : `Synced from ${payload.sourceEnvironment}`
}

// Spreading a whole template into String.fromCodePoint overflows the call stack.
const BASE64_CHUNK_SIZE = 0x8000

function toBase64Url(bytes: Uint8Array): string {
  let binary = ""
  for (let i = 0; i < bytes.length; i += BASE64_CHUNK_SIZE) {
    binary += String.fromCodePoint(...bytes.subarray(i, i + BASE64_CHUNK_SIZE))
  }
  let base64 = btoa(binary).replaceAll("+", "-").replaceAll("/", "_")
  while (base64.endsWith("=")) base64 = base64.slice(0, -1)
  return base64
}

function fromBase64Url(value: string): Uint8Array<ArrayBuffer> {
  const base64 = value.replaceAll("-", "+").replaceAll("_", "/")
  const binary = atob(base64.padEnd(Math.ceil(base64.length / 4) * 4, "="))
  return Uint8Array.from(binary, (char) => char.codePointAt(0) ?? 0)
}

async function transform(
  bytes: Uint8Array<ArrayBuffer>,
  stream: CompressionStream | DecompressionStream,
): Promise<Uint8Array> {
  const source = new ReadableStream<Uint8Array<ArrayBuffer>>({
    start(controller) {
      controller.enqueue(bytes)
      controller.close()
    },
  })
  return new Uint8Array(
    await new Response(source.pipeThrough(stream)).arrayBuffer(),
  )
}

function isSyncPayload(value: unknown): value is IEmailTemplateSyncPayload {
  if (!value || typeof value !== "object") return false
  const payload = value as Record<string, unknown>
  return (
    typeof payload.communicationName === "string" &&
    typeof payload.subject === "string" &&
    typeof payload.htmlContent === "string" &&
    typeof payload.plainContent === "string" &&
    typeof payload.makeLive === "boolean" &&
    (payload.modifiedReason === null ||
      typeof payload.modifiedReason === "string") &&
    Object.values(SyncEnvironment).includes(
      payload.sourceEnvironment as SyncEnvironment,
    )
  )
}

export async function encodeSyncPayload(
  payload: IEmailTemplateSyncPayload,
): Promise<string> {
  const json = new TextEncoder().encode(JSON.stringify(payload))
  return toBase64Url(
    await transform(json, new CompressionStream("deflate-raw")),
  )
}

/** Null when the value is not a payload produced by encodeSyncPayload. */
export async function decodeSyncPayload(
  encoded: string,
): Promise<IEmailTemplateSyncPayload | null> {
  try {
    const bytes = await transform(
      fromBase64Url(encoded),
      new DecompressionStream("deflate-raw"),
    )
    const parsed: unknown = JSON.parse(new TextDecoder().decode(bytes))
    return isSyncPayload(parsed) ? parsed : null
  } catch {
    return null
  }
}

// The payload rides in the fragment: it never reaches the server, so template
// size isn't capped by request-line limits.
export async function buildSyncUrl(
  payload: IEmailTemplateSyncPayload,
  target: SyncEnvironment = syncTargetEnvironment(),
): Promise<string> {
  return `${NOVA_URLS[target]}${SYNC_ROUTE_PATH}#${await encodeSyncPayload(payload)}`
}
